function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useContext, useState, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Tag } from '../../Tag';
import { ENTER_KEY, SPACE_KEYCODE } from '../../Utils/constant';
import { SelectMenuContext } from '../context';
import { SelectMenuTypes, SelectOptionTypes, removePlaceholder, getContainerHeight } from '../helper';
import SelectMenuComponent from '../SelectMenu/SelectMenuBody/SelectMenuComponent';
import VirtualizedList from '../VirtualizedList';
import styles from '@dtsl/css-select-menu/dist/index.module.css';
import ChevronLeft from '@dtsl/icons/dist/icons/react/ChevronLeft';
import ChevronRight from '@dtsl/icons/dist/icons/react/ChevronRight';
import Folder from '@dtsl/icons/dist/icons/react/Folder';
function FolderSelectionList(props) {
  const {
    options = [],
    onSelect = () => {},
    togglePanel = false,
    handleParentNavigation = () => {},
    handleChildNavigation = () => {},
    readonly = false
  } = props;
  const {
    placeHolder,
    selectedFolderValue,
    selectedValues,
    selectMenuChildFolderRef,
    setFocused,
    enableVirtualization
  } = useContext(SelectMenuContext);
  const [slideRightAnimation, setSlideRightAnimation] = useState(false);
  const [slideLeftAnimation, setSlideLeftAnimation] = useState(false);
  const onParentNavigatation = (option, e) => {
    setSlideRightAnimation(true);
    handleParentNavigation(option, e);
  };
  const onChildNavigatation = (option, e) => {
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    setSlideLeftAnimation(true);
    handleChildNavigation(option, e);
  };
  const renderOption = option => {
    let {
      key = key,
      icon = () => {},
      helpText,
      label,
      value,
      disabled = false,
      items = [],
      addtionalInfo = () => {},
      customLabelStyle = {}
    } = option;
    let isSelected = value === (selectedValues[0] || {}).value && label === (selectedValues[0] || {}).label && key === (selectedValues[0] || {}).key;
    const optionClass = classNames({
      [styles['sib-selectmenu-option']]: true,
      [styles['is-hovered']]: isSelected,
      [styles['selected']]: isSelected,
      [styles['locked']]: disabled,
      [option.className]: !!option.className
    });
    const labelClass = classNames({
      [styles['sib-selectmenu-option__label']]: true,
      [styles['sib-typo_text-body']]: !isSelected,
      [styles['sib-typo_text-body--bold']]: isSelected
    });
    const addInfo = addtionalInfo();
    const iconFunc = icon();
    let numberOfSelected = 0;
    if (props.selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) {
      const totalSelectedValues = removePlaceholder(selectedValues, placeHolder);
      const totalAvailableOptions = items.reduce((acc, curr) => {
        if (curr.type === SelectOptionTypes.GROUP_LIST) {
          const {
            items = []
          } = curr;
          items.forEach(item => {
            if (!item.disabled) {
              acc.push({
                ...item
              });
            }
          });
        } else {
          if (!curr.disabled) {
            acc.push({
              ...curr
            });
          }
        }
        return acc;
      }, []);
      const selectedOptions = totalAvailableOptions.filter(option => {
        return totalSelectedValues.some(value => option.key === value.key && option.value === value.value);
      });
      numberOfSelected = selectedOptions.length;
    }
    return /*#__PURE__*/React.createElement("li", {
      key: key,
      className: optionClass,
      role: "option",
      "aria-selected": isSelected ? 'true' : 'false',
      onClick: !disabled ? e => onParentNavigatation(option, e) : null,
      onKeyDown: event => {
        if ((event.key === ENTER_KEY || event.keyCode === SPACE_KEYCODE) && !disabled) {
          onParentNavigatation(option, event);
          setFocused(null);
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      key: key,
      title: label,
      className: styles['sib-selectmenu-option__content'],
      disabled: disabled
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-selectmenu-option__content-left']
    }, /*#__PURE__*/React.createElement("span", {
      className: labelClass
    }, iconFunc ? /*#__PURE__*/React.createElement("span", {
      className: styles['sib-selectmenu-option__icon']
    }, icon()) : /*#__PURE__*/React.createElement("span", {
      className: styles['sib-selectmenu-option__icon']
    }, /*#__PURE__*/React.createElement(Folder, null)), /*#__PURE__*/React.createElement("span", {
      style: customLabelStyle
    }, label)), helpText && /*#__PURE__*/React.createElement("span", {
      className: `${styles['sib-selectmenu-option__helptext']} ${styles['sib-add-space']}`
    }, helpText)), addInfo && /*#__PURE__*/React.createElement("span", {
      className: styles['additional-info']
    }, addtionalInfo()), numberOfSelected > 0 && /*#__PURE__*/React.createElement(Tag, {
      label: String(numberOfSelected),
      disabled: disabled || readonly
    }), /*#__PURE__*/React.createElement("span", {
      className: styles['sib-selectmenu-option__icon']
    }, /*#__PURE__*/React.createElement(ChevronRight, null))));
  };
  const {
    labelClass,
    label,
    addtionalInfo,
    icon = () => {}
  } = selectedFolderValue;
  const parentFolderClass = classNames({
    [styles['sib-selectmenu-options']]: true,
    [styles['sib-selectmenu-folder-parent']]: true,
    [styles['slide-right']]: slideRightAnimation || slideLeftAnimation,
    [styles['sib-is-selectmenu-virtualised-options']]: enableVirtualization
  });
  const childFolderClass = classNames({
    [styles['sib-selectmenu-options']]: true,
    [styles['sib-selectmenu-folder-child']]: true,
    [styles['slide-left']]: slideLeftAnimation || slideRightAnimation,
    [styles['sib-is-selectmenu-virtualised-options']]: enableVirtualization
  });
  const iconFunc = icon();
  const listHeight = useMemo(() => getContainerHeight(options), [options]);
  const folderOptionsJSX = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("ul", {
    role: "presentation",
    className: parentFolderClass,
    tabIndex: "0",
    style: {
      display: `${togglePanel ? 'none' : 'block'}`
    }
  }, enableVirtualization ? /*#__PURE__*/React.createElement(VirtualizedList, {
    itemContent: (index, option) => renderOption(option),
    data: options,
    defaultHeight: listHeight
  }) : options.map(option => {
    return renderOption(option);
  })), /*#__PURE__*/React.createElement("ul", {
    ref: selectMenuChildFolderRef,
    className: childFolderClass,
    tabIndex: props.selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION ? '0' : null,
    role: "presentation",
    style: {
      display: `${togglePanel ? 'block' : 'none'}`
    }
  }, /*#__PURE__*/React.createElement("li", {
    tabIndex: props.selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION ? '0' : null,
    className: styles['sib-selectmenu-option-folder-header'],
    role: "option",
    onClick: e => onChildNavigatation({}, e),
    onKeyDown: event => {
      if (event.key === ENTER_KEY || event.keyCode === SPACE_KEYCODE) {
        onChildNavigatation({}, event);
        setFocused(null);
      }
    },
    "data-test": "selectMenu-list",
    "aria-selected": "false"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-selectmenu-option__content']
  }, /*#__PURE__*/React.createElement("div", {
    className: `${styles['sib-selectmenu-option__content-left']} ${styles['sib-selectmenu-flex-center']}`
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['sib-selectmenu-option__icon']
  }, /*#__PURE__*/React.createElement(ChevronLeft, null)), /*#__PURE__*/React.createElement("span", {
    className: `${styles['sib-selectmenu-flex-center']} ${labelClass}`
  }, iconFunc ? /*#__PURE__*/React.createElement("span", {
    className: styles['sib-selectmenu-option__icon']
  }, icon()) : /*#__PURE__*/React.createElement("span", {
    className: styles['sib-selectmenu-option__icon']
  }, /*#__PURE__*/React.createElement(Folder, null)), /*#__PURE__*/React.createElement("span", null, label))), addtionalInfo && /*#__PURE__*/React.createElement("span", {
    className: styles['additional-info']
  }, addtionalInfo()))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(SelectMenuComponent, _extends({}, props, {
    isSubFolder: true,
    onSelect: onSelect,
    options: selectedFolderValue.items,
    selectMenuType: props.selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION ? SelectMenuTypes.SINGLE_SELECTION : SelectMenuTypes.MULTI_SELECTION
  })))));
  return folderOptionsJSX;
}
FolderSelectionList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    icon: PropTypes.func,
    value: PropTypes.string,
    helpText: PropTypes.string,
    addtionalInfo: PropTypes.func,
    items: PropTypes.array,
    customLabelStyle: PropTypes.object
  })),
  togglePanel: PropTypes.bool,
  handleParentNavigation: PropTypes.func,
  handleChildNavigation: PropTypes.func,
  addInfo: PropTypes.func,
  onSelect: PropTypes.func,
  heading: PropTypes.string,
  items: PropTypes.array,
  disabled: PropTypes.bool,
  selectMenuType: PropTypes.string,
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  fireChangeEvent: PropTypes.func.isRequired,
  readonly: PropTypes.bool
};
export default FolderSelectionList;